'use client';

import { useState, useRef, useEffect, useTransition, useActionState } from 'react';
import { Button } from './Button';
import { AvatarStars } from './AvatarStars';
import { Instrument, InstrumentLabels } from '@/app/instruments';
import { LeadSource, leadSourceLabels } from '@/config/leadSourceLabels';
import { AgeGroup, AgeGroupLabels } from '@/config/agegroupLabels';
import { capture } from '@/config/captureUserEvent';
import * as fpixel from '@/lib/fpixel';
import { META_EVENT_ID_FIELD, metaMatchKeys, newMetaEventId } from '@/lib/metaLead';
import { EventNames } from '@/config/eventNames';
import { z } from 'zod';
import { submitQuickLead } from './actions/submitQuickLead.action';
import { SuccessIcon } from './SuccessIcon';
import Spinner from './Spinner';
import Confetti from 'react-confetti';
import useWindowSize from 'react-use/lib/useWindowSize';
import { useSearchParams } from 'next/navigation';
import {
  prepareEnhancedConversionData,
  normalizeEmail,
  normalizePhone,
} from '@/utils/enhancedConversions';
import posthog from 'posthog-js';
import {
  getAdAttribution,
  classifyAcquisition,
  TRACKING_IDS,
  UTM_KEYS,
  type AdAttribution,
  type TrackingIdKey,
} from '@/utils/adAttribution';
import { readStagedDraft, clearStagedDraft } from '@/lib/webmcp/pendingDraft';
import { serialiseTrail } from '@/lib/webmcp/trail';
import { errorFor, failedFields, invalidFieldNames, unplacedErrors, type FormMode } from '@/lib/forms/fieldErrors';

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p data-field-error role="alert" className="mt-2 text-sm text-red-600">
      {message}
    </p>
  );
}

type CheckedField = 'name' | 'email' | 'phone';

/**
 * At least the server's bar, so a tick never promises what submit then rejects. The
 * phone check is stricter than the server's 8 characters: "0412 345" passes that,
 * and a tick on a half-typed number tells the visitor they are done when they are not.
 */
const looksValid: Record<CheckedField, (value: string, country?: string) => boolean> = {
  name: (value) => value.trim().length > 0,
  email: (value) => z.string().email().safeParse(value).success,
  phone: (value, country = 'AU') => {
    const digits = value.replace(/\D/g, '');
    return country === 'AU' ? /^(0\d{9}|61\d{9})$/.test(digits) : /^1?\d{10}$/.test(digits);
  },
};

function ValidMark({ show }: { show?: boolean }) {
  if (!show) return null;
  return (
    <span
      data-valid-mark
      aria-hidden="true"
      className="pointer-events-none absolute right-0 top-0 flex h-9 items-center pr-3 text-green-600"
    >
      <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
        <path
          fillRule="evenodd"
          d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 1 1 1.4-1.4l3.8 3.79 6.8-6.8a1 1 0 0 1 1.4 0Z"
          clipRule="evenodd"
        />
      </svg>
    </span>
  );
}

/** Choices, not personal details: safe to report with their values when a form is abandoned. */
const OPEN_FIELDS = /^(instrument|ageGroup|leadSource|location|country|parentInstrument|students\[\d+\]\[(instrument|ageGroup)\])$/;

function classNames(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

interface QuickLeadsFormProps {
  onClose: () => void;
  autoFocus?: boolean;
  instrument?: Instrument;
}

interface StudentEntry {
  name: string;
  instrument: Instrument;
  ageGroup: AgeGroup;
}

const shouldPrefillForm = process.env.NEXT_PUBLIC_FLAG_PREFILL_FORM === 'true';
const prefillEmail = process.env.NEXT_PUBLIC_FLAG_PREFILL_EMAIL || 'test@musiceternal.life';
const prefillPhone = process.env.NEXT_PUBLIC_FLAG_PREFILL_PHONE || '';

const famousMusicians = [
  'Taylor Swift',
  'Ed Sheeran',
  'Billie Eilish',
  'Bruno Mars',
  'Adele',
  'John Mayer',
  'Elton John',
  'Freddie Mercury',
  'David Bowie',
  'Prince',
  'Stevie Wonder',
  'Beyoncé',
  'Lady Gaga',
  'Harry Styles',
  'Dua Lipa',
];

function getRandomMusician(): string {
  return famousMusicians[Math.floor(Math.random() * famousMusicians.length)];
}

function generatePrefillEmail(baseEmail: string, suffix: string): string {
  const atIndex = baseEmail.lastIndexOf('@');
  if (atIndex === -1) return `${baseEmail}+${suffix}@example.com`;
  const local = baseEmail.slice(0, atIndex);
  const domain = baseEmail.slice(atIndex + 1);
  return `${local}+${suffix}@${domain}`;
}

export default function QuickLeadsForm(props: QuickLeadsFormProps) {
  // Generate random suffix once for both name and email when prefill is enabled
  const randomSuffix = shouldPrefillForm ? Math.random().toString(36).substring(2, 15) : '';

  // Form mode
  const [isFamilyEnrollment, setIsFamilyEnrollment] = useState(false);

  // Single student fields
  const [name, setName] = useState(
    shouldPrefillForm ? getRandomMusician() : ''
  );
  const [instrument, setInstrument] = useState<Instrument>(
    props.instrument ?? 'piano'
  );
  const [ageGroup, setAgeGroup] = useState<AgeGroup | ''>('');

  // Shared fields
  const [email, setEmail] = useState(
    shouldPrefillForm ? generatePrefillEmail(prefillEmail, randomSuffix) : ''
  );
  const [phone, setPhone] = useState(shouldPrefillForm ? prefillPhone : '');
  const [country, setCountry] = useState('AU');
  const [leadSource, setLeadSource] = useState<LeadSource | ''>('');
  const [location, setLocation] = useState('studio');
  const [submitted, setSubmitted] = useState(false);

  // Name of person booking (optional)
  const [bookerName, setBookerName] = useState('');

  // Family enrollment fields
  const [parentName, setParentName] = useState('');
  const [parentIsAlsoStudent, setParentIsAlsoStudent] = useState(false);
  const [parentInstrument, setParentInstrument] = useState<Instrument>('piano');
  const [students, setStudents] = useState<StudentEntry[]>([
    { name: '', instrument: props.instrument ?? 'piano', ageGroup: 'primaryschool' }
  ]);

  const { width, height } = useWindowSize();
  const searchParams = useSearchParams();

  const [attribution, setAttribution] = useState<AdAttribution | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Persist what arrived on this URL so a popup opened later in the session
    // still carries it.
    for (const id of [...TRACKING_IDS, ...UTM_KEYS]) {
      const value = searchParams.get(id);
      if (value) sessionStorage.setItem(id, value);
    }
    const sessionReader = {
      getItem: (k: string) => sessionStorage.getItem(k),
    };
    setAttribution(
      getAdAttribution({
        urlParams: new URLSearchParams(window.location.search),
        posthog: posthog.__loaded ? posthog : null,
        cookies: document.cookie,
        sessionStorage: sessionReader,
      }),
    );
  }, [searchParams]);

  const lastTouch = attribution?.lastTouch ?? {};
  const firstTouch = attribution?.firstTouch ?? {};

  const formRef = useRef<HTMLFormElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const instrumentRef = useRef<HTMLSelectElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const aboutRef = useRef<HTMLTextAreaElement>(null);

  // These two mark how far down the form someone got, so they have to land
  // before freeTrialFormCompleted. Hashing is async and would let them arrive
  // after it, which drops the person out of an ordered funnel; normalising is
  // not, so these carry the normalised value and leave the hashes to the
  // conversion events. Latched because blur alone loses anyone who mistypes,
  // fixes it on submit, and never blurs the field again.
  const emailReached = useRef(false);
  const phoneReached = useRef(false);

  function markEmailReached(value: string): void {
    if (emailReached.current) return;
    if (!z.string().email().safeParse(value).success) return;
    emailReached.current = true;
    capture(EventNames.freeTrialFieldInputEmailAddress, {
      email: value,
      isQuickLead: true,
      normalized_email: normalizeEmail(value),
    });
  }

  function markPhoneReached(value: string): void {
    if (phoneReached.current) return;
    if (!z.string().min(8).safeParse(value).success) return;
    phoneReached.current = true;
    capture(EventNames.freeTrialFieldInputPhoneNumber, {
      phone: value,
      isQuickLead: true,
      normalized_phone: normalizePhone(value, country),
    });
  }

  // Fields the visitor has left at least once. The tick waits for this so it
  // confirms a finished answer rather than flickering on and off while they type.
  const [left, setLeft] = useState<Partial<Record<CheckedField, boolean>>>({});

  function markLeft(field: CheckedField): void {
    setLeft((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
  }

  // Set when an in-page agent staged these details rather than the visitor
  // typing them. It changes how the lead is attributed, never what is asked.
  const [agentAssisted, setAgentAssisted] = useState(false);

  useEffect(() => {
    if (props.autoFocus && instrumentRef.current) {
      instrumentRef.current.focus();
    }
  }, [props.autoFocus]);

  // Someone who opens the form, starts it and leaves without sending was invisible:
  // form_blocked only fires on a submit press. Reports which fields they filled, in
  // form order, on close or when the tab goes. Personal fields are named only; their
  // values never leave the visitor's screen.
  // True from the moment the visitor sends until the server says it failed, so a tab
  // closed mid-submit, or the success screen unmounting, is never reported as abandoned.
  const sentRef = useRef(false);

  useEffect(() => {
    // Re-run per form instance: after a booking, OK brings back a fresh, empty form.
    const form = formRef.current;
    if (!form) return;
    sentRef.current = false;

    const openedAt = Date.now();
    const touched = new Set<string>();
    const track = (event: Event) => {
      const target = event.target as HTMLInputElement | null;
      if (target?.name) touched.add(target.name);
    };
    let reported = false;
    // 'hidden' is the one iPhones reliably deliver: switching apps or locking the phone
    // hides the tab without a pagehide. A visitor who comes back and books still sends
    // form_completed afterwards, so read abandoned as "left at some point".
    const report = (trigger: 'hidden' | 'pagehide' | 'closed') => {
      if (reported || sentRef.current) return;
      const filled: string[] = [];
      const choices: Record<string, string> = {};
      for (const element of Array.from(form.elements) as HTMLInputElement[]) {
        const { name, value, type, checked } = element;
        // An unticked radio or checkbox still carries a value; only the chosen one counts.
        if ((type === 'radio' || type === 'checkbox') && !checked) continue;
        if (!name || !touched.has(name) || !value.trim() || filled.includes(name)) continue;
        filled.push(name);
        if (OPEN_FIELDS.test(name)) choices[name] = value;
      }
      // Under a second open with nothing typed is a double tap, or React's dev remount, not a visitor leaving.
      if (filled.length === 0 && Date.now() - openedAt < 1000) return;
      reported = true;
      capture(
        EventNames.freeTrialFormAbandoned,
        {
          // Empty when they opened the form and typed nothing: a drop-off too.
          filled,
          last_field: filled[filled.length - 1] ?? null,
          choices,
          trigger,
          isQuickLead: true,
          isFamilyEnrollment: form.querySelector<HTMLInputElement>('input[name="kind"]')?.value === 'family',
        },
        // A page on its way out drops ordinary requests; a beacon survives it.
        trigger === 'closed' ? undefined : { transport: 'sendBeacon' },
      );
    };
    const onPageHide = () => report('pagehide');
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') report('hidden');
    };

    form.addEventListener('input', track, true);
    form.addEventListener('change', track, true);
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      form.removeEventListener('input', track, true);
      form.removeEventListener('change', track, true);
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
      report('closed');
    };
  }, [submitted]);

  // An agent may have staged the visitor's details before this form existed:
  // the modal is unmounted until someone opens it. Filling the fields is all
  // that happens here - the visitor still presses the button themselves.
  useEffect(() => {
    const staged = readStagedDraft();
    if (!staged) return;

    const { draft } = staged;
    setName(draft.name);
    setEmail(draft.email);
    setPhone(draft.phone);
    setInstrument(draft.instrument);
    setAgeGroup(draft.ageGroup);
    setCountry(draft.country);
    setLocation(draft.location);
    setLeadSource(draft.leadSource);
    if (draft.about && aboutRef.current) aboutRef.current.value = draft.about;
    setAgentAssisted(true);
  }, []);

  const [isPending, startTransition] = useTransition();
  const [state, formAction] = useActionState(submitQuickLead, null);
  // The response belongs to the mode that was submitted, not whichever tab is open now:
  // without this a family error reappears in the student tab, for a field it has not got.
  const [submittedMode, setSubmittedMode] = useState<FormMode>('student');
  const mode: FormMode = isFamilyEnrollment ? 'family' : 'student';

  // One id per submission, shared by the browser and server Meta Lead so they count once.
  const metaEventIdRef = useRef('');

  function handleSubmit(payload: FormData): void {
    setSubmittedMode(mode);
    metaEventIdRef.current = newMetaEventId();
    payload.set(META_EVENT_ID_FIELD, metaEventIdRef.current);
    startTransition(() => {
      formAction(payload);
    });
  }

  useEffect(() => {
    // A rejected send is back in the visitor's hands; leaving now is an abandon.
    if (state && !state.done) sentRef.current = false;
  }, [state]);

  useEffect(() => {
    if (state?.done) {
      sentRef.current = true;
      setSubmitted(true);
      clearStagedDraft();
      fpixel.lead(
        metaEventIdRef.current,
        metaMatchKeys(email, normalizePhone(phone, country), isFamilyEnrollment ? parentName : name, country),
      );
      const displayName = isFamilyEnrollment ? parentName : name;
      // Prepare enhanced conversion data for confirmation
      prepareEnhancedConversionData(email, phone, country).then(enhancedData => {
        capture(EventNames.freeTrialBookingConfirmationViewed, {
          name: displayName,
          email,
          phone,
          instrument: isFamilyEnrollment ? students.map(s => s.instrument).join(', ') : instrument,
          isQuickLead: true,
          isFamilyEnrollment,
          hashed_email: enhancedData.hashed_email,
          hashed_phone: enhancedData.hashed_phone,
          normalized_email: enhancedData.normalized_email,
          normalized_phone: enhancedData.normalized_phone,
          // Carried as properties, never as a second event name: a separate
          // name would drop these out of the ordered funnel's conversion step
          // the same way the late field events did.
          lead_channel: agentAssisted ? 'agent' : 'human',
          acquisition: classifyAcquisition(attribution?.lastTouch ?? {}),
        });
      });
    }
  }, [state, email, phone, country, name, instrument, parentName, students, isFamilyEnrollment, agentAssisted, attribution]);

  useEffect(() => {
    if (!state || state.done || !state.errors) return;
    // Field names only. Values would put phone numbers and names into analytics.
    capture(EventNames.freeTrialFormFailed, {
      fields: failedFields(state.errors),
      isFamilyEnrollment: submittedMode === 'family',
      isQuickLead: true,
    });
    // On a phone the submit button is at the bottom and the message may be above the
    // fold, which reads exactly like the old bug: pressing submit and nothing happening.
    formRef.current
      ?.querySelector('[data-field-error]')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Fire once per response, not when the person edits a field afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const handleNext = async () => {
    if (formRef.current && !formRef.current.checkValidity()) {
      // The browser stops these before the server ever sees them, so without this the
      // most likely place a parent gives up - an empty required box - leaves no trace.
      capture(EventNames.freeTrialFormBlocked, {
        fields: invalidFieldNames(formRef.current),
        isFamilyEnrollment,
        isQuickLead: true,
      });
    }
    if (formRef.current?.reportValidity()) {
      const displayName = isFamilyEnrollment ? parentName : name;
      const displayInstrument = isFamilyEnrollment
        ? students.map(s => InstrumentLabels[s.instrument]).join(', ')
        : instrument;

      // Autofill and typo-then-fix both reach here without a qualifying blur.
      markEmailReached(email);
      markPhoneReached(phone);

      // Prepare enhanced conversion data
      const enhancedData = await prepareEnhancedConversionData(
        email,
        phone,
        country
      );

      capture(EventNames.freeTrialFormCompleted, {
        name: displayName,
        email,
        phone,
        instrument: displayInstrument,
        isQuickLead: true,
        isFamilyEnrollment,
        studentCount: isFamilyEnrollment ? students.length : 1,
        hashed_email: enhancedData.hashed_email,
        hashed_phone: enhancedData.hashed_phone,
        normalized_email: enhancedData.normalized_email,
        normalized_phone: enhancedData.normalized_phone,
      });

      sentRef.current = true;
      formRef.current.requestSubmit();
    }
  };

  const addStudent = () => {
    if (students.length < 3) {
      setStudents([...students, { name: '', instrument: props.instrument ?? 'piano', ageGroup: 'primaryschool' }]);
    }
  };

  const removeStudent = (index: number) => {
    if (students.length > 1) {
      setStudents(students.filter((_, i) => i !== index));
    }
  };

  const updateStudent = (index: number, field: keyof StudentEntry, value: string) => {
    const updated = [...students];
    if (field === 'instrument') {
      updated[index][field] = value as Instrument;
    } else if (field === 'ageGroup') {
      updated[index][field] = value as AgeGroup;
    } else {
      updated[index][field] = value;
    }
    setStudents(updated);
  };

  const displayName = isFamilyEnrollment ? parentName : name;
  const displayInstrument = isFamilyEnrollment
    ? students.map(s => InstrumentLabels[s.instrument]).join(', ')
    : InstrumentLabels[instrument];

  if (submitted) {
    return (
      <div className="flex flex-col justify-center items-center py-6 my-auto gap-6 h-[466px] relative">
        <SuccessIcon />
        <h4 className="text-2xl">Thanks {(bookerName || displayName).split(' ')[0]}! 🎉</h4>
        <h5 className="text-lg font-bold px-8">
          We&apos;ll Contact You Within 1 Business Hour.
        </h5>
        <p className="px-16 text-center">
          Our team will reach out to you at <strong>{email}</strong> or{' '}
          <strong>{phone}</strong> to schedule {isFamilyEnrollment ? 'trial lessons' : 'your free trial lesson'} at a time
          that works best for you.
        </p>
        <p className="px-16 text-center">
          Get ready to start your {displayInstrument} journey!
        </p>
        <p className="px-16">Talk to you soon! 🎶</p>

        <div className="flex justify-end gap-4 border-t-[1px] border-t-gray-200 border-solid py-4 px-4 w-full">
          <Button
            className="w-full sm:w-auto"
            role="button"
            type="button"
            color="green"
            onClick={() => {
              props.onClose?.();
              setTimeout(() => setSubmitted(false), 500);
            }}
          >
            OK
          </Button>
        </div>

        <Confetti
          drawShape={(ctx) => {
            ctx.beginPath();
            ctx.arc(0, 0, 10, 0, 2 * Math.PI);
            ctx.fill();
            ctx.moveTo(10, 0);
            ctx.lineTo(10, -40);
            ctx.stroke();
            ctx.moveTo(10, -40);
            ctx.quadraticCurveTo(20, -50, 10, -60);
            ctx.stroke();
            ctx.closePath();
          }}
          recycle={false}
          width={width}
          height={height}
        />
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      className="flex flex-col h-full max-h-[85vh] w-full relative"
      action={handleSubmit}
    >
      {isPending && (
        <>
          <div className="absolute bg-green-200 opacity-20 w-full h-full z-10"></div>
          <div className="absolute m-auto top-[50dvh] left-[50dvw] sm:top-1/2 sm:left-1/2 -translate-x-16 -translate-y-20 z-20">
            <Spinner />
            <div className="my-3 bg-white rounded-lg">...Submitting...</div>
          </div>
        </>
      )}

      {/* Header and fields scroll together, only the button stays pinned: a fixed
          header took about half a phone screen, before the keyboard took the rest. */}
      <div className={classNames('flex-1 overflow-y-auto', isPending && 'blur-sm')}>
      <div>
        <div className="flex flex-col justify-center items-center py-2">
          <AvatarStars />
        </div>

        <div className="text-start text-sm leading-6 px-6">
          <h3 className="text-lg font-semibold text-center mb-2">
            Get A Free Trial Lesson
          </h3>
          <p className="text-center text-gray-600 mb-2 text-md">
            Tell us a bit about yourself and we&apos;ll contact you within 1 business hour
            to personally schedule your free trial!
          </p>
          <p className="text-xs text-gray-500 leading-relaxed text-center mb-4">
            By submitting, you agree to hear from Music Eternal about your trial and community updates. Unsubscribe anytime.{' '}
            <a
              href="https://portal.musiceternal.life/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-700"
            >
              Privacy Policy
            </a>
            .
          </p>

          {state?.errors?.general && (
            <div
              data-field-error
              role="alert"
              className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-start"
            >
              <p className="text-sm font-semibold text-red-700">
                We could not confirm your booking
              </p>
              <p className="mt-1 text-sm text-red-600">{state.errors.general}</p>
              <p className="mt-2 text-sm text-red-700">
                <a href="tel:+61489998339" className="font-semibold underline">
                  Call 0489 998 339
                </a>
                {' or '}
                <a
                  href="mailto:help@musiceternal.life"
                  className="font-semibold underline"
                >
                  email us
                </a>
                . You can also try the form again.
              </p>
            </div>
          )}

          {submittedMode === mode && unplacedErrors(state?.errors, mode).map(({ key, message }) => (
            <p key={key} data-field-error role="alert" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-start text-sm text-red-700">
              {message}
            </p>
          ))}

          {/* Enrollment type tabs */}
          <div className="flex rounded-lg bg-gray-100 p-1 mb-4">
            <button
              type="button"
              onClick={() => setIsFamilyEnrollment(false)}
              className={classNames(
                'flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all duration-200',
                !isFamilyEnrollment
                  ? 'bg-white text-green-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              Single Student
            </button>
            <button
              type="button"
              onClick={() => setIsFamilyEnrollment(true)}
              className={classNames(
                'flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all duration-200',
                isFamilyEnrollment
                  ? 'bg-white text-green-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              Family / Friends<span className="hidden sm:inline"> (up to 3)</span>
            </button>
          </div>
        </div>
      </div>

      {/* Hidden field for kind */}
      <input type="hidden" name="kind" value={isFamilyEnrollment ? 'family' : 'student'} />

      <div className="px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-4 text-start">
          {/* Single student mode */}
          {!isFamilyEnrollment && (
            <>
              {/* Easy choices first, contact details last: people quit at the personal
                  fields, so ask them once they have already put something in. */}
              <div>
                <label
                  htmlFor="instrument"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Which Instrument Are You Interested In?*
                </label>
                <div className="mt-2">
                  <select
                    required
                    value={instrument}
                    onChange={(e) => setInstrument(e.target.value as Instrument)}
                    ref={instrumentRef}
                    id="instrument"
                    name="instrument"
                    autoFocus={props.autoFocus}
                    autoComplete="instrument-name"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                  >
                    {Object.entries(InstrumentLabels).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
                <FieldError message={errorFor(state?.errors, 'instrument')} />
              </div>

              <div>
                <label
                  htmlFor="ageGroup"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Student&apos;s Age Group*
                </label>
                <div className="mt-2">
                  <select
                    required
                    value={ageGroup}
                    onChange={(e) => setAgeGroup(e.target.value as AgeGroup)}
                    id="ageGroup"
                    name="ageGroup"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                  >
                    <option value="" disabled>Please select...</option>
                    {Object.entries(AgeGroupLabels).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
                <FieldError message={errorFor(state?.errors, 'ageGroup')} />
              </div>

              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Student&apos;s Full Name*
                </label>
                <div className="relative mt-2">
                  <input
                    ref={nameInputRef}
                    required
                    id="name"
                    name="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={(e) => {
                      markLeft('name');
                      if (e.target.value.length > 1) {
                        capture(EventNames.freeTrialFieldInputStudentName, {
                          name: e.target.value,
                          isQuickLead: true,
                        });
                      }
                    }}
                    autoComplete="name"
                    className="block w-full rounded-md border-0 py-1.5 pr-9 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                    placeholder="e.g. Taylor Swift"
                  />
                  <ValidMark show={left.name && looksValid.name(name)} />
                </div>
                <FieldError message={errorFor(state?.errors, 'name')} />
              </div>

              <div>
                <label
                  htmlFor="bookerName"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Name of Person Booking (optional)
                </label>
                <div className="mt-2">
                  <input
                    id="bookerName"
                    name="bookerName"
                    type="text"
                    value={bookerName}
                    onChange={(e) => setBookerName(e.target.value)}
                    autoComplete="name"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                    placeholder='e.g. "Sarah Smith (Mum)"'
                  />
                </div>
              </div>
            </>
          )}

          {/* Family enrollment mode */}
          {isFamilyEnrollment && (
            <>
              {/* Parent section */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 sm:col-span-2">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Parent/Guardian/Friend Details</h4>

                <div className="mb-3">
                  <label
                    htmlFor="parentName"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    Parent/Guardian/Friend Name*
                  </label>
                  <div className="mt-2">
                    <input
                      ref={nameInputRef}
                      required
                      id="parentName"
                      name="parentName"
                      type="text"
                      value={parentName}
                      onChange={(e) => setParentName(e.target.value)}
                      autoFocus={props.autoFocus}
                      autoComplete="name"
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                      placeholder="e.g. Sarah Smith"
                    />
                  </div>
                  <FieldError message={errorFor(state?.errors, 'parentName')} />
                </div>

                <div className="mb-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="parentIsAlsoStudent"
                      checked={parentIsAlsoStudent}
                      onChange={(e) => setParentIsAlsoStudent(e.target.checked)}
                      value="true"
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700">
                      I also want lessons
                    </span>
                  </label>
                </div>

                {parentIsAlsoStudent && (
                  <div>
                    <label
                      htmlFor="parentInstrument"
                      className="block text-sm font-medium leading-6 text-gray-900"
                    >
                      Your Instrument*
                    </label>
                    <div className="mt-2">
                      <select
                        required={parentIsAlsoStudent}
                        value={parentInstrument}
                        onChange={(e) => setParentInstrument(e.target.value as Instrument)}
                        id="parentInstrument"
                        name="parentInstrument"
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                      >
                        {Object.entries(InstrumentLabels).map(([key, value]) => (
                          <option key={key} value={key}>
                            {value}
                          </option>
                        ))}
                      </select>
                    </div>
                    <FieldError message={errorFor(state?.errors, 'parentInstrument')} />
                  </div>
                )}
              </div>

              {/* Students section */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 sm:col-span-2">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-semibold text-gray-700">Students</h4>
                  {students.length < 3 && (
                    <button
                      type="button"
                      onClick={addStudent}
                      className="text-sm text-green-600 hover:text-green-700 font-medium"
                    >
                      + Add another student
                    </button>
                  )}
                </div>

                {students.map((student, index) => (
                  <div key={index} className="mb-4 p-3 bg-white rounded border border-gray-200 last:mb-0">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-medium text-gray-500">Student {index + 1}</span>
                      {students.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeStudent(index)}
                          className="text-xs text-red-500 hover:text-red-600"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Name*
                        </label>
                        <input
                          required
                          type="text"
                          name={`students[${index}][name]`}
                          value={student.name}
                          onChange={(e) => updateStudent(index, 'name', e.target.value)}
                          placeholder="Student name"
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Instrument*
                        </label>
                        <select
                          required
                          name={`students[${index}][instrument]`}
                          value={student.instrument}
                          onChange={(e) => updateStudent(index, 'instrument', e.target.value)}
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                        >
                          {Object.entries(InstrumentLabels).map(([key, value]) => (
                            <option key={key} value={key}>
                              {value}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Age Group*
                        </label>
                        <select
                          required
                          name={`students[${index}][ageGroup]`}
                          value={student.ageGroup}
                          onChange={(e) => updateStudent(index, 'ageGroup', e.target.value)}
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                        >
                          {Object.entries(AgeGroupLabels).map(([key, value]) => (
                            <option key={key} value={key}>
                              {value}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
                <FieldError message={errorFor(state?.errors, 'students')} />
              </div>
            </>
          )}

          {/* Shared fields */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Email Address*
            </label>
            <div className="relative mt-2">
              <input
                required
                ref={emailInputRef}
                onBlur={(e) => {
                  markEmailReached(e.target.value);
                  markLeft('email');
                }}
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder="email@example.com"
                className={classNames(
                  state?.errors?.email && 'focus:ring-red-600 ring-red-600',
                  'block w-full rounded-md border-0 py-1.5 pr-9 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6'
                )}
              />
              <ValidMark show={left.email && looksValid.email(email)} />
              <FieldError message={errorFor(state?.errors, 'email')} />
            </div>
          </div>

          <div>
            <label
              htmlFor="phone"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Phone Number*
            </label>
            <div className="relative mt-2 rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 flex items-center">
                <label htmlFor="country" className="sr-only">
                  Country
                </label>
                <select
                  required
                  id="country"
                  name="country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  autoComplete="country"
                  className="h-full rounded-md border-0 bg-transparent py-0 pl-3 pr-7 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm"
                >
                  <option value="AU">AU</option>
                  <option value="US">US</option>
                  <option value="CA">CA</option>
                </select>
              </div>
              <input
                required
                id="phone"
                name="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onBlur={(e) => {
                  markPhoneReached(e.target.value);
                  markLeft('phone');
                }}
                placeholder="0482 072 096"
                className="block w-full rounded-md border-0 py-1.5 pl-20 pr-9 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
              />
              <ValidMark show={left.phone && looksValid.phone(phone, country)} />
            </div>
            <FieldError message={errorFor(state?.errors, 'phone')} />
          </div>

          <div>
            <label
              htmlFor="leadSource"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Where Did You First Hear About Us?*
            </label>
            <div className="mt-2">
              <select
                required
                value={leadSource}
                onChange={(e) => setLeadSource(e.target.value as LeadSource)}
                id="leadSource"
                name="leadSource"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
              >
                <option value="" disabled>Please select...</option>
                {Object.entries(leadSourceLabels).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
            <FieldError message={errorFor(state?.errors, 'leadSource')} />
          </div>

          <div>
            <label className="block text-sm font-medium leading-6 text-gray-900">
              Preferred Location*
            </label>
            <div className="mt-2 space-y-2">
              {[
                { id: 'studio', title: 'Studio (687a Brighton Rd Seacliff)' },
                { id: 'online', title: 'Online' },
              ].map((loc) => (
                <div key={loc.id} className="flex items-center">
                  <input
                    checked={loc.id === location}
                    id={loc.id}
                    name="location"
                    value={loc.id}
                    onChange={(e) => setLocation(e.target.value)}
                    type="radio"
                    className="h-4 w-4 border-gray-300 text-green-600 focus:ring-green-600"
                  />
                  <label htmlFor={loc.id} className="ml-3 text-sm font-medium text-gray-900 cursor-pointer">
                    {loc.title}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2">
            <label
              htmlFor="about"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Anything else you&apos;d like us to know?*
            </label>
            <div className="mt-2">
              <textarea
                required
                ref={aboutRef}
                id="about"
                name="about"
                rows={2}
                placeholder={isFamilyEnrollment
                  ? "e.g. Ages of students, experience levels, scheduling preferences..."
                  : "e.g. Complete beginner, student's age group, always wanted to learn. Or: Had lessons years ago, looking to pick it back up..."}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>

          {/* Hidden fields for tracking parameters: legacy flat (last-touch) + first/last touch variants */}
          {(TRACKING_IDS as readonly TrackingIdKey[]).map((id) => (
            <span key={id}>
              <input type="hidden" name={id} value={lastTouch[id] ?? ''} />
              <input type="hidden" name={`${id}_last_touch`} value={lastTouch[id] ?? ''} />
              <input type="hidden" name={`${id}_first_touch`} value={firstTouch[id] ?? ''} />
            </span>
          ))}
          <input
            type="hidden"
            name="attribution_trail"
            value={serialiseTrail(attribution, agentAssisted ? 'webmcp' : null)}
          />
        </div>
      </div>
      </div>

      {/* Fixed footer */}
      <div className={classNames('flex-shrink-0 flex flex-col items-stretch sm:items-end gap-2 border-t border-gray-200 py-4 px-6 bg-white', isPending && 'blur-sm')}>
        <Button
          className="w-full sm:w-auto"
          role="button"
          type="button"
          color="green"
          onClick={handleNext}
          disabled={isPending}
        >
          {isPending
            ? 'Submitting...'
            : isFamilyEnrollment
              ? `Register ${students.length} Student${students.length > 1 ? 's' : ''} for Free Trial`
              : 'Get Your Free Trial Lesson'}
        </Button>
        <p className="text-xs text-gray-500 text-center sm:text-right">
          We&apos;ll text or call to arrange a time.
        </p>
      </div>
    </form>
  );
}
